import { z } from "zod";
import { versionField } from "./common";

/**
 * Live video (user, 2026-11-03; migration 0086). A video link is saved PER
 * MEET by OVR operators (`meets.stream_url`, `stream_featured`), and ONE
 * featured link per games (`featured_streams`: ceremonies, the studio show)
 * by SUPER_ADMIN. The portal plays them only while something is live.
 *
 *   PATCH /meets/:id                          stream_url / stream_featured
 *   POST  /meets/:id/stream/apply-to-event    StreamApplyResult
 *   GET   /featured-streams                   FeaturedStreamGames[]
 *   PUT   /featured-streams/:games_id         FeaturedStream
 *   GET   /public/live-videos?games=sea|para  LiveVideos (public/live-videos.ts)
 *
 * THE one link parser is `parseStreamUrl`, here — the API validates every
 * body through `makeStreamUrlField` and the feed builds every embed with it.
 * SUKMA's mistakes, not repeated: no server-side validation (a stray quote got
 * saved), any URL iframed, the parser hand-copied in three apps.
 *
 * An ALLOWLIST: YouTube and Facebook videos only. Anything else is refused —
 * never "unknown host, iframe it anyway".
 */

// ─── Limits ──────────────────────────────────────────────────────────────────

/** The longest STORED link (the canonical form; `ck_*_stream_url`). */
export const STREAM_URL_MAX = 500;
/** The longest link a body may carry before it is parsed (tracking parameters make pasted links long). */
export const STREAM_URL_INPUT_MAX = 2000;
/** A featured stream's title ("Opening Ceremony"). */
export const FEATURED_STREAM_TITLE_MAX = 120;

export const STREAM_PLATFORMS = ["youtube", "facebook"] as const;
export type StreamPlatform = (typeof STREAM_PLATFORMS)[number];

/**
 * Why a link was refused:
 *   not_url          not a link at all
 *   not_https        an http:// (or other scheme) link
 *   unsupported_host not YouTube or Facebook
 *   youtube_handle   a channel's @handle / /c/ / /user/ page — it cannot be
 *                    embedded; paste the live video's own link (or /channel/UC…)
 *   no_video_id      a YouTube / Facebook link that names no video
 */
export const STREAM_URL_REFUSALS = ["not_url", "not_https", "unsupported_host", "youtube_handle", "no_video_id"] as const;
export type StreamUrlRefusal = (typeof STREAM_URL_REFUSALS)[number];

export type ParsedStreamUrl =
	| { ok: true; url: string; platform: StreamPlatform; embedSrc: string }
	| { ok: false; reason: StreamUrlRefusal };

// ─── The parser ──────────────────────────────────────────────────────────────

/** The WHATWG URL parser every runtime here has (Node, browsers); contracts compile without DOM / Node types. */
interface QueryParams {
	get(name: string): string | null;
}
interface ParsedUrl {
	protocol: string;
	hostname: string;
	username: string;
	password: string;
	port: string;
	pathname: string;
	searchParams: QueryParams;
}
const WhatwgUrl = (globalThis as unknown as { URL: new (input: string) => ParsedUrl }).URL;

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const YT_CHANNEL = /^UC[A-Za-z0-9_-]{22}$/;
const FB_ID = /^\d{5,25}$/;
const FB_WATCH_ID = /^[A-Za-z0-9_-]{5,40}$/;
/** `www.` / `m.` / `music.` are the same site (the mobile and YouTube Music copies). */
const HOST_PREFIX = /^(?:www\.|m\.|music\.)/;
/** Quotes and angle brackets a paste drags along (`"https://…"`, `<https://…>`), and the spaces around them. */
const WRAPPING = /^[\s<>"'`]+|[\s<>"'`]+$/g;

const refuse = (reason: StreamUrlRefusal): ParsedStreamUrl => ({ ok: false, reason });

const youtube = (id: string): ParsedStreamUrl => ({
	ok: true,
	platform: "youtube",
	url: `https://www.youtube.com/watch?v=${id}`,
	embedSrc: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&rel=0&playsinline=1`,
});

/** A channel's CURRENT live stream (whatever video it is) — the one YouTube page without a video id that embeds. */
const youtubeChannel = (channel: string): ParsedStreamUrl => ({
	ok: true,
	platform: "youtube",
	url: `https://www.youtube.com/channel/${channel}/live`,
	embedSrc: `https://www.youtube.com/embed/live_stream?channel=${channel}&autoplay=1&mute=1`,
});

const facebook = (canonical: string): ParsedStreamUrl => ({
	ok: true,
	platform: "facebook",
	url: canonical,
	embedSrc: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(canonical)}&show_text=false&autoplay=true&mute=true`,
});

function parseYoutube(host: string, parts: string[], params: QueryParams): ParsedStreamUrl {
	if (host === "youtu.be") {
		const id = parts[0] ?? "";
		return YT_ID.test(id) ? youtube(id) : refuse("no_video_id");
	}
	const [first = "", second = "", third] = parts;
	if (first.startsWith("@") || first === "c" || first === "user") return refuse("youtube_handle");
	if (first === "watch") {
		const id = params.get("v") ?? "";
		return YT_ID.test(id) ? youtube(id) : refuse("no_video_id");
	}
	if (first === "embed" && second === "live_stream") {
		const channel = params.get("channel") ?? "";
		return YT_CHANNEL.test(channel) ? youtubeChannel(channel) : refuse("no_video_id");
	}
	if (first === "live" || first === "embed" || first === "shorts" || first === "v") {
		return YT_ID.test(second) ? youtube(second) : refuse("no_video_id");
	}
	if (first === "channel") {
		if (!YT_CHANNEL.test(second)) return refuse("no_video_id");
		return third === undefined || third === "live" ? youtubeChannel(second) : refuse("no_video_id");
	}
	return refuse("no_video_id");
}

function parseFacebook(host: string, parts: string[], params: QueryParams): ParsedStreamUrl {
	if (host === "fb.watch") {
		const id = parts[0] ?? "";
		return parts.length === 1 && FB_WATCH_ID.test(id) ? facebook(`https://fb.watch/${id}/`) : refuse("no_video_id");
	}
	if (parts[0] === "watch") {
		const id = params.get("v") ?? "";
		return FB_ID.test(id) ? facebook(`https://www.facebook.com/watch/?v=${id}`) : refuse("no_video_id");
	}
	// /<page>/videos/<id>, or /<page>/videos/<title-slug>/<id> (older share links).
	const at = parts.indexOf("videos");
	if (at === 1) {
		const page = parts[0]!;
		const id = parts.slice(2).find((p) => FB_ID.test(p));
		if (id && /^[A-Za-z0-9.\-_]+$/.test(page)) return facebook(`https://www.facebook.com/${page}/videos/${id}`);
	}
	return refuse("no_video_id");
}

/**
 * A pasted link → its canonical form, platform and the embed the portal
 * iframes; or why it is refused. Trims, strips wrapping quotes / angle
 * brackets, adds `https://` when there is no scheme, ignores `www.` / `m.` /
 * `music.`. Pure: the API's body field, the feed's mapper and OVR's preview
 * all call this one function.
 */
export function parseStreamUrl(raw: string): ParsedStreamUrl {
	const text = raw.replace(WRAPPING, "");
	if (text === "" || text.length > STREAM_URL_INPUT_MAX || /\s/.test(text)) return refuse("not_url");
	const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(text) && !/^[^/:]+:\d+(?:[/?#]|$)/.test(text);
	if (hasScheme && !/^https:\/\//i.test(text)) return /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? refuse("not_https") : refuse("not_url");
	let url: ParsedUrl;
	try {
		url = new WhatwgUrl(hasScheme ? text : `https://${text}`);
	} catch {
		return refuse("not_url");
	}
	if (url.protocol !== "https:" || !url.hostname.includes(".")) return refuse("not_url");
	if (url.username || url.password || url.port) return refuse("unsupported_host");
	const host = url.hostname.toLowerCase().replace(HOST_PREFIX, "");
	const parts = url.pathname.split("/").filter((p: string) => p !== "");
	if (host === "youtube.com" || host === "youtu.be" || host === "youtube-nocookie.com") return parseYoutube(host, parts, url.searchParams);
	if (host === "facebook.com" || host === "fb.watch") return parseFacebook(host, parts, url.searchParams);
	return refuse("unsupported_host");
}

// ─── Body fields ─────────────────────────────────────────────────────────────

export interface StreamFormMessages extends Record<StreamUrlRefusal, string> {
	urlRequired: string;
	titleRequired: string;
	titleTooLong: string;
}

export const enStreamFormMessages: StreamFormMessages = {
	not_url: "Enter a video link",
	not_https: "The link must start with https://",
	unsupported_host: "Only YouTube and Facebook video links can be shown",
	youtube_handle: "A channel page cannot be shown. Paste the link of the live video itself",
	no_video_id: "This link does not point to a video",
	urlRequired: "Enter a video link",
	titleRequired: "Enter a title",
	titleTooLong: `${FEATURED_STREAM_TITLE_MAX} characters at most`,
};

const parsedOrIssue = (m: StreamFormMessages) => (raw: string, ctx: z.RefinementCtx) => {
	const parsed = parseStreamUrl(raw);
	if (!parsed.ok) {
		ctx.addIssue({ code: z.ZodIssueCode.custom, message: m[parsed.reason], params: { reason: parsed.reason } });
		return z.NEVER;
	}
	return parsed.url;
};

/**
 * A meet's `stream_url` in a body: trimmed FIRST (bug 5), `""` (or `null`) =
 * clear it, anything else must parse — and is stored as the CANONICAL link,
 * so what the database holds is always one `parseStreamUrl` accepts.
 */
export function makeStreamUrlField(m: StreamFormMessages = enStreamFormMessages) {
	return z
		.string()
		.trim()
		.max(STREAM_URL_INPUT_MAX, m.not_url)
		.transform((raw, ctx) => (raw === "" ? null : parsedOrIssue(m)(raw, ctx)))
		.nullable();
}
export const streamUrlField = makeStreamUrlField();

/** A link that must be there (a featured stream): the same rules, `""` refused. */
export function makeRequiredStreamUrlField(m: StreamFormMessages = enStreamFormMessages) {
	return z
		.string()
		.trim()
		.min(1, m.urlRequired)
		.max(STREAM_URL_INPUT_MAX, m.not_url)
		.transform((raw, ctx) => parsedOrIssue(m)(raw, ctx));
}

/** The audit headline — the API's `reasonField` rule (trimmed, 1–300). */
const reasonText = z.string().trim().min(1).max(300);

/**
 * `POST /meets/:id/stream/apply-to-event` — copy this meet's link to the
 * event's OTHER meets that are not cancelled, that the caller may write, whose
 * event is not locked and whose link differs. `dry_run: true` counts them and
 * writes nothing (the same WHERE), so the confirm dialog's number is the write's.
 */
export const streamApplyToEventBody = z.object({ dry_run: z.boolean() }).strict();
export type StreamApplyToEventBody = z.input<typeof streamApplyToEventBody>;

/** `dry_run: true` → `{ count }`; otherwise `{ updated }`. */
export type StreamApplyResult = { count: number } | { updated: number };

/**
 * `PUT /featured-streams/:games_id` — SUPER_ADMIN. An upsert: `version` is
 * the one the client loaded (omit it when the games has no row yet); another
 * one — or none while a row exists — is 409 STALE_VERSION. Sending what is
 * stored writes nothing.
 */
export function makePutFeaturedStreamBody(m: StreamFormMessages = enStreamFormMessages) {
	return z.object({
		url: makeRequiredStreamUrlField(m),
		title: z.string().trim().min(1, m.titleRequired).max(FEATURED_STREAM_TITLE_MAX, m.titleTooLong),
		enabled: z.boolean(),
		version: versionField.optional(),
		reason: reasonText,
	});
}
export const putFeaturedStreamBody = makePutFeaturedStreamBody();
export type PutFeaturedStreamBody = z.input<typeof putFeaturedStreamBody>;

/** One games' featured stream, as stored. */
export interface FeaturedStream {
	games_id: string;
	/** The canonical link (`parseStreamUrl(...).url`). */
	url: string;
	platform: StreamPlatform;
	title: string;
	enabled: boolean;
	version: number;
	created_at: string;
	updated_at: string;
}

/** `GET /featured-streams` — every games (two), each with its row or null. */
export interface FeaturedStreamGames {
	games_id: string;
	code: string;
	name: string;
	featured_stream: FeaturedStream | null;
}
